import React, { useState, useMemo, useCallback, useEffect, lazy, Suspense } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Plus, Search, LayoutGrid, FolderPlus, MoreHorizontal,
} from 'lucide-react';
import { pagesAPI, billingAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useThemeStore } from '../../store/themeStore';

// Lazy-load heavy children so first paint is fast
const PageBuilderPage = lazy(() => import('./PageBuilderPage'));
const TemplateGalleryPage = lazy(() => import('./TemplateGalleryPage'));

/* ------------------------------------------------------------------ */
/*  Context for passing selectedPageId to children without re-mounting */
/* ------------------------------------------------------------------ */
export interface BuilderContextValue {
  selectedPageId: string | null;
  setSelectedPageId: (id: string | null) => void;
}

export const BuilderContext = React.createContext<BuilderContextValue>({
  selectedPageId: null,
  setSelectedPageId: () => {},
});

/**
 * Unified layout for all /builder/* routes.
 *
 * Owns the "which page is selected" state so clicking a page in the
 * sidebar is a *state change*, not a route change.  This prevents the
 * editor component from unmounting / remounting (which caused the
 * visible "div jumping up and down").
 *
 * The URL is kept in sync via history.replaceState (no navigation, no
 * route transitions) so bookmarks & refresh still work.
 */
export const BuilderLayout: React.FC = () => {
  const { t } = useTranslation();
  const { '*': splat } = useParams();          // e.g. "abc123" or "templates" or ""
  const { user } = useAuthStore();
  const { theme } = useThemeStore();
  const pc = theme.hex.primary;

  /* ---- Derive initial view from URL on mount (useState initializer runs once) ---- */
  const [selectedPageId, setSelectedPageIdRaw] = useState<string | null>(() => {
    if (!splat || splat === '' || splat === 'templates') {
      return null;
    }
    return splat.split('/')[0];
  });
  const [view, setView] = useState<'templates' | 'editor'>(() => {
    if (!splat || splat === '' || splat === 'templates') {
      return 'templates';
    }
    return 'editor';
  });
  const [pageSearchQuery, setPageSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'All' | 'My' | 'Manager'>('All');

  /* ---- Sync URL silently when selectedPageId changes ---- */
  const setSelectedPageId = useCallback((id: string | null) => {
    setSelectedPageIdRaw(id);
    if (id) {
      setView('editor');
      window.history.replaceState(null, '', `/builder/${id}`);
    } else {
      setView('templates');
      window.history.replaceState(null, '', '/builder/templates');
    }
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname;
      if (path === '/builder' || path === '/builder/' || path === '/builder/templates') {
        setSelectedPageIdRaw(null);
        setView('templates');
      } else {
        const match = path.match(/^\/builder\/([^/]+)/);
        if (match) {
          setSelectedPageIdRaw(match[1]);
          setView('editor');
        }
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /* ---- Queries ---- */
  const { data: pagesData } = useQuery({
    queryKey: ['pages'],
    queryFn: () => pagesAPI.list().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: subData } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => billingAPI.subscription().then(r => r.data),
    staleTime: 60 * 60 * 1000,
  });

  const pages = useMemo(() => (pagesData?.pages || []) as Array<Record<string, unknown>>, [pagesData?.pages]);

  const filteredPages = useMemo(() => {
    if (!pageSearchQuery.trim()) {
      return pages;
    }
    const q = pageSearchQuery.toLowerCase();
    return pages.filter(p => String(p.title || '').toLowerCase().includes(q));
  }, [pages, pageSearchQuery]);

  const handleCreatePage = useCallback(() => {
    setSelectedPageIdRaw(null);
    setView('templates');
    window.history.replaceState(null, '', '/builder/templates');
  }, []);

  const contextValue = useMemo<BuilderContextValue>(() => ({
    selectedPageId,
    setSelectedPageId,
  }), [selectedPageId, setSelectedPageId]);

  return (
    <BuilderContext.Provider value={contextValue}>
      <div className="h-full w-full flex bg-white overflow-hidden">
        {/* ─── Unified Sidebar ─── */}
        <div className="w-56 bg-white border-r border-gray-200 flex flex-col shrink-0">
          {/* Create button */}
          <div className="p-3 border-b border-gray-200">
            <button
              onClick={handleCreatePage}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold transition-colors hover:opacity-90"
              style={{ backgroundColor: pc }}
            >
              <Plus className="h-3.5 w-3.5" />
              {t('page_create_button', 'Create page')}
            </button>
          </div>

          {/* Search */}
          <div className="px-3 py-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                value={pageSearchQuery}
                onChange={(e) => setPageSearchQuery(e.target.value)}
                placeholder={t('page_search_placeholder', 'Search in pages')}
                className="w-full pl-8 pr-3 py-2 text-[12px] font-medium bg-slate-50 border border-slate-200/60 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300 transition-all placeholder-slate-400"
              />
            </div>
          </div>

          {/* Tabs: All / My / Manager */}
          <div className="px-3 border-b border-gray-200 flex items-center gap-3 text-xs mb-2">
            {(['All', 'My', 'Manager'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-2 border-b-2 font-semibold transition-colors ${
                  activeTab === tab
                    ? 'text-slate-900'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                }`}
                style={activeTab === tab ? { borderColor: pc } : undefined}
              >
                {tab}
              </button>
            ))}
            <button className="ml-auto pb-2 text-slate-400 hover:text-slate-600 transition-colors">
              <FolderPlus className="w-4 h-4" />
            </button>
          </div>

          {/* New group */}
          <div className="px-2 pb-2">
            <button className="w-full flex items-center gap-2 px-2.5 py-2 bg-gray-50 rounded-lg text-xs text-gray-600 hover:bg-gray-100 transition-colors font-medium">
              <FolderPlus className="w-3.5 h-3.5 text-gray-400" />
              {t('page_new_group', 'New group')}
              <div className="ml-auto w-5 h-5 bg-white rounded flex items-center justify-center shadow-sm border border-slate-200/60">
                <MoreHorizontal className="w-3 h-3 text-slate-400" />
              </div>
            </button>
          </div>

          {/* Page list */}
          <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {filteredPages.map((page) => {
              const isActive = selectedPageId === (page.id as string);
              return (
                <button
                  key={page.id as string}
                  onClick={() => setSelectedPageId(page.id as string)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all duration-150 ${
                    isActive ? 'bg-gray-100 border border-gray-200' : 'hover:bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ${
                    isActive ? 'bg-white shadow-sm' : 'bg-gray-100'
                  }`}>
                    {page.thumbnail ? (
                      <img src={page.thumbnail as string} alt={page.title as string} className="w-full h-full object-cover" />
                    ) : (
                      <LayoutGrid className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-semibold truncate ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                      {page.title as string}
                    </p>
                    <p className="text-[10px] text-gray-400 truncate font-mono">/{page.slug as string}</p>
                  </div>
                  {(page.is_published as boolean | undefined) && <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: pc }} />}
                </button>
              );
            })}
            {pages.length === 0 && (
              <div className="text-center py-8 px-4">
                <LayoutGrid className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs text-gray-500">{t('page_no_results', 'No pages yet')}</p>
              </div>
            )}
          </div>

          {/* Quota Footer */}
          <div className="p-3 border-t border-gray-200 bg-gray-50/50">
            <div className="flex items-center justify-between text-[10px] font-semibold text-gray-500 mb-2">
              <span>Pages: {pages.length}/{(subData?.limits?.pages || (user as unknown as Record<string, unknown>)?.max_pages || 1) as number}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="h-1.5 rounded-full transition-all duration-500"
                style={{
                  backgroundColor: pc,
                  width: `${Math.min((pages.length / ((subData?.limits?.pages || (user as unknown as Record<string, unknown>)?.max_pages || 1) as number)) * 100, 100)}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* ─── Center + Right content ─── */}
        <Suspense fallback={<div className="flex-1 flex items-center justify-center bg-gray-50"><p className="text-sm text-gray-400">{t('loading', 'Loading...')}</p></div>}>
          {view === 'editor' && selectedPageId ? (
            <PageBuilderPage />
          ) : (
            <TemplateGalleryPage />
          )}
        </Suspense>
      </div>
    </BuilderContext.Provider>
  );
};

export default BuilderLayout;
